import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Preload map: current route -> chunks to prefetch
const PRELOAD_MAP: Record<string, Array<() => Promise<any>>> = {
  "/": [
    // Landing page -> likely to go to auth or dashboard
    () => import(/* webpackChunkName: 'auth' */ "../pages/Auth"),
    () => import(/* webpackChunkName: 'dashboard' */ "../pages/Dashboard"),
  ],
  "/auth": [
    // Auth page -> definitely going to dashboard after login
    () => import(/* webpackChunkName: 'dashboard' */ "../pages/Dashboard"),
    () => import(/* webpackChunkName: 'people' */ "../pages/MyPeople"),
  ],
  "/dashboard": [
    // Dashboard -> likely to check people, schedule, or messages
    () => import(/* webpackChunkName: 'people' */ "../pages/MyPeople"),
    () => import(/* webpackChunkName: 'events' */ "../pages/MyEvents"),
  ],
  "/people": [
    // People -> might explore connections or messages
    () => import(/* webpackChunkName: 'connections' */ "../pages/Connections"),
    () => import(/* webpackChunkName: 'messages' */ "../pages/Messages"),
  ],
};

/**
 * Hook that preloads route chunks based on the current location
 * Intelligently prefetches likely next destinations to improve navigation speed
 */
export function useRoutePreloader() {
  const location = useLocation();

  useEffect(() => {
    const preloadChunks = PRELOAD_MAP[location.pathname];
    
    if (!preloadChunks) return;

    // Use requestIdleCallback to preload during idle time
    const idleWindow = window as typeof window & {
      requestIdleCallback?: (cb: IdleRequestCallback) => number;
    };

    const preloadTask = () => {
      preloadChunks.forEach((importFn) => {
        // Trigger the import without awaiting - this starts the fetch
        importFn().catch(() => {
          // Silently catch errors - preloading is non-critical
        });
      });
    };

    if (idleWindow.requestIdleCallback) {
      const id = idleWindow.requestIdleCallback(preloadTask);
      return () => idleWindow.cancelIdleCallback?.(id);
    } else {
      // Fallback: slight delay before preloading
      const timeout = setTimeout(preloadTask, 100);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);
}
